import { useEffect, useMemo, useRef, useState } from 'react'
import {
  useStoriesQuery,
  sortStoriesUnviewedFirst,
  isStoryViewed,
  sendEventsBatched,
  trackStoryLoadFailed,
  type StoryTrayShownEvent,
} from '@entities/stories'
import { StoryViewer } from './StoryViewer'
import { LockIcon } from './icons'
import styles from './StoriesTray.module.css'

/** story_tray_shown отправляется ОДИН раз за запуск приложения. */
let trayShownSent = false

/**
 * Лента кружков-превью историй: грузит ленту, сортирует непросмотренные вперёд,
 * по тапу открывает {@link StoryViewer}. Пропсов нет — данные тянет сам.
 */
export const StoriesTray = () => {
  const { data, isLoading, error } = useStoriesQuery()
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const trayRef = useRef<HTMLDivElement>(null)

  const ordered = useMemo(
    () => sortStoriesUnviewedFirst(data?.stories ?? []),
    [data],
  )

  // Аналитика: лента не загрузилась. story_id не шлём — не приехала вся лента.
  const loadFailSentRef = useRef(false)
  useEffect(() => {
    if (!error || loadFailSentRef.current) return
    loadFailSentRef.current = true
    trackStoryLoadFailed(error)
  }, [error])

  // Аналитика: лента показана — один раз за запуск, с флагом видимости кружков.
  useEffect(() => {
    if (trayShownSent || ordered.length === 0) return
    trayShownSent = true
    requestAnimationFrame(() => {
      const vw = window.innerWidth
      const els = trayRef.current
        ? Array.from(trayRef.current.querySelectorAll<HTMLElement>(`.${styles.circle}`))
        : []
      const event: StoryTrayShownEvent = {
        name: 'story_tray_shown',
        stories: ordered.slice(0, 10).map((story, i) => {
          const el = els[i]
          let isVisible = true
          if (el) {
            const r = el.getBoundingClientRect()
            isVisible = r.left < vw && r.right > 0
          }
          return {
            story_id: story.story_id,
            position: i + 1,
            is_visible: isVisible,
            has_access: story.has_access,
            is_completed: isStoryViewed(story),
          }
        }),
      }
      sendEventsBatched([event])
    })
  }, [ordered])

  if (isLoading) {
    return <div className={styles.state}>Loading stories…</div>
  }
  if (error) {
    return <div className={styles.state}>Couldn’t load stories</div>
  }
  if (ordered.length === 0) {
    // Пустое состояние (макета пока нет — заглушка).
    return <div className={styles.state}>No stories yet</div>
  }

  return (
    <>
      <div className={styles.tray} role="list" ref={trayRef}>
        {ordered.map((story, index) => {
          const viewed = isStoryViewed(story)
          const locked = !story.has_access
          const photoClass = [
            styles.photo,
            (!viewed || locked) && styles.photoActive,
            locked && styles.photoLocked,
          ]
            .filter(Boolean)
            .join(' ')
          return (
            <button
              key={story.story_id}
              type="button"
              role="listitem"
              className={styles.circle}
              onClick={() => setOpenIndex(index)}
            >
              <span className={photoClass}>
                <img
                  src={story.icon_url}
                  alt=""
                  className={styles.img}
                  loading="lazy"
                />
                {locked && (
                  <span className={styles.lock}>
                    <LockIcon />
                  </span>
                )}
              </span>
              <span className={styles.title}>{story.title}</span>
            </button>
          )
        })}
      </div>

      {openIndex !== null && (
        <StoryViewer
          stories={ordered}
          startIndex={openIndex}
          onClose={() => setOpenIndex(null)}
        />
      )}
    </>
  )
}

export default StoriesTray
